/**
 * How one live session went: its numbers, who came, the questions left with replies to send, the busiest moments
 * on the room's curve, who reacted most, and where the replay has got to. The room page's Last live tab and a
 * session's own report both draw it.
 */
import { useNavigate } from "react-router";
import { Button } from "../../components/ui/Button";
import { Face, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import type { WebinarLive } from "../../../shared/types";
import { askedAt, longDayTime, replaySteps } from "./liveWords";
import { RepliesSheet } from "./RepliesSheet";
import { ReplaySheet } from "./ReplaySheet";
import { mins, useSessionReport } from "./useSessionReport";

const n = (v: number) => v.toLocaleString("en-US");
const WALL = 20;

/** The room minute by minute, with the busiest moments marked on it. */
function RoomCurve({ inRoom, lengthSec, marks }: { inRoom: readonly number[]; lengthSec: number; marks: Array<{ at: number }> }) {
  const pts = inRoom.length > 1 ? inRoom : [0, 0];
  const top = Math.max(4, ...pts);
  const step = Math.max(1, Math.ceil(top / 3 / 5) * 5);
  const ticks = [0, step, step * 2, step * 3];
  const max = step * 3;
  const X = (i: number) => 40 + (i * 740) / (pts.length - 1);
  const Y = (v: number) => 170 - (v / max) * 148;
  const line = pts.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${X(pts.length - 1)} 170 L${X(0)} 170 Z`;
  const L = Math.max(1, lengthSec);
  const atX = (sec: number) => 40 + (Math.min(sec, L) / L) * 740;
  const atY = (sec: number) => Y(pts[Math.min(pts.length - 1, Math.round((sec / L) * (pts.length - 1)))] ?? 0);
  const total = mins(L);
  const axis = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(total * f));
  return (
    <svg className="chart" viewBox="0 0 800 196" preserveAspectRatio="none" role="img" aria-label={`People in the room over ${total} minutes`}>
      <defs>
        <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--wine-ink)" stopOpacity=".28" />
          <stop offset="1" stopColor="var(--wine-ink)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line x1="40" x2="780" y1={Y(v)} y2={Y(v)} stroke="var(--line)" strokeWidth="1" />
          <text x="30" y={Y(v) + 4} textAnchor="end">
            {v}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#ag)" />
      <path d={line} fill="none" stroke="var(--wine-ink)" strokeWidth="2.5" strokeLinejoin="round" />
      {marks.map((m, i) => (
        <g key={m.at}>
          <line x1={atX(m.at)} x2={atX(m.at)} y1="22" y2="170" stroke="var(--brass)" strokeWidth="1.5" strokeDasharray="3 4" />
          <circle cx={atX(m.at)} cy={atY(m.at)} r="5" fill="var(--surface)" stroke="var(--brass)" strokeWidth="2.5" />
          <text x={atX(m.at) + 6} y="16" className="mark">
            {i + 1} · {mins(m.at)} min
          </text>
        </g>
      ))}
      {axis.map((m, i) => (
        <text key={i} x={40 + (i / 4) * 740} y="190" textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"}>
          {m} min
        </text>
      ))}
    </svg>
  );
}

export function LastLiveTab({
  webinarId,
  title,
  live,
  seriesSession,
}: {
  webinarId: string;
  title: string;
  /** The session, as its own read answers: ended, with its report. */
  live: WebinarLive;
  /** True when it is one session of a series: its number shows. */
  seriesSession: boolean;
}) {
  const navigate = useNavigate();
  const { came, reactions, markAnswered, answered, replying, setReplying, playing, setPlaying, sessionId, report, marks, chapters, waiting, lengthSec, pill, playable, stayedPct, openThread } =
    useSessionReport(webinarId, live, seriesSession);
  const seeList = () => navigate(`/rooms/${webinarId}`, { state: { group: "came", session: sessionId } });
  // Why one number: "came live", "Who came" and its "+N" all read the session's own count, the faces its first few.
  const wall = (came?.people ?? []).slice(0, Math.min(WALL, live.cameLive));

  return (
    <>
      <section className="card">
        <div className="card-h">
          <div>
            <h2>{longDayTime(live.startsAt, live.timezone)}</h2>
            {seriesSession ? <div className="sub">Session {live.seq}</div> : null}
          </div>
          <Button size="s" icon="play" disabled={!playable} title={playable ? undefined : "The replay is still being made"} onClick={() => setPlaying(0)}>
            Watch the replay
          </Button>
        </div>
        <div className="card-b">
          <div className="stat4">
            <div className="stat">
              <b>{n(live.cameLive)}</b>
              <span>Came live</span>
              <em>of {n(live.registered)} registered</em>
            </div>
            <div className="stat">
              <b>{mins(report?.averageStaySec ?? 0)} min</b>
              <span>Average stay</span>
              <em>of {mins(lengthSec)} minutes</em>
            </div>
            <div className="stat">
              <b>{n(live.stayedToEnd)}</b>
              <span>Stayed to the end</span>
              <em>{stayedPct}% of those who came</em>
            </div>
            <div className="stat">
              <b>{n(report?.mostAtOnce ?? live.mostAtOnce)}</b>
              <span>Most at once</span>
              {report ? <em>at {mins(report.mostAtOnceAt)} min</em> : null}
            </div>
          </div>
        </div>
      </section>

      <section className="card mt20">
        <div className="card-h">
          <div>
            <h2>Who came</h2>
            <div className="sub">
              {n(live.cameLive)} {live.cameLive === 1 ? "person" : "people"}
            </div>
          </div>
          {live.cameLive ? (
            <button type="button" className="link" onClick={seeList}>
              See them as a list <Icon name="chev" />
            </button>
          ) : null}
        </div>
        <div className="card-b">
          {live.cameLive === 0 ? (
            <p className="small muted">No one came to this one.</p>
          ) : (
            <div className="facewall">
              {wall.map((p) => (
                <button key={p.registrantId} type="button" className="facebtn" title={p.name} onClick={() => navigate(`/people/${p.registrantId}`)}>
                  <Ring name={p.name} photo={p.avatar_url} size={40} percent={p.pct} />
                </button>
              ))}
              {came && live.cameLive > wall.length ? (
                <button type="button" className="face more wallmore" title={`See all ${n(live.cameLive)}`} onClick={seeList}>
                  +{n(live.cameLive - wall.length)}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <section className="card mt20">
        <div className="card-h">
          <div>
            <h2>Questions</h2>
            <div className="sub">
              {live.questions.length === 0
                ? "No one asked a question"
                : waiting.length
                  ? `${n(waiting.length)} still ${waiting.length === 1 ? "needs" : "need"} an answer`
                  : "All answered"}
            </div>
          </div>
          {waiting.length ? (
            <Button size="s" variant="p" icon="sparkle" onClick={() => setReplying(true)}>
              Send replies
            </Button>
          ) : null}
        </div>
        {live.questions.length ? (
          <div className="card-b" style={{ paddingTop: 8 }}>
            {live.questions.map((q) => (
              <div key={q.id} className="qrow">
                <Face name={q.name} size={38} />
                <div>
                  <div className="row g8">
                    <b><bdi>{q.name}</bdi></b>
                    <span className="xs muted">{askedAt(q.at)}</span>
                  </div>
                  <div className="mt4">“{q.body}”</div>
                </div>
                {q.answered || answered.includes(q.id) ? (
                  <Pill tone="good" dot>
                    {q.answered === "live" ? "Answered live" : "Answered"}
                  </Pill>
                ) : (
                  <Button size="s" variant="soft" icon="chat" onClick={() => void openThread(q)}>
                    Answer
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ height: 18 }} />
        )}
      </section>

      {report ? (
        <section className="card mt20">
          <div className="card-h">
            <div>
              <h2>The busiest moments</h2>
            </div>
          </div>
          <div className="card-b">
            <RoomCurve inRoom={report.inRoom} lengthSec={lengthSec} marks={marks} />
            {marks.length ? (
              <div className="grid g3 mt12">
                {marks.map((m, i) => (
                  <button key={m.at} type="button" className="note brass marknote" disabled={!playable} onClick={() => setPlaying(Math.max(0, m.at - 4))}>
                    <b className="brass">{i + 1}</b>
                    <div>
                      <b>{mins(m.at)} min</b>
                      <br />
                      {m.words}
                      {m.extra ? `. ${m.extra}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="grid g2 mt20">
        <section className="card">
          <div className="card-h">
            <h2>Who reacted most</h2>
          </div>
          <div className="card-b col" style={{ gap: 12 }}>
            {reactions && reactions.reactors.length ? (
              reactions.reactors.slice(0, 3).map((r) => (
                <button key={r.registrantId} type="button" className="who whobtn" onClick={() => navigate(`/people/${r.registrantId}`)}>
                  <Face name={r.name} size={36} />
                  <div className="sp">
                    <div className="nm"><bdi>{r.name}</bdi></div>
                    <div className="ds">
                      {n(r.taps)} {r.taps === 1 ? "reaction" : "reactions"}
                    </div>
                  </div>
                  <Icon name="heart" />
                </button>
              ))
            ) : (
              <p className="small muted">{reactions ? "No one reacted this time." : "Loading…"}</p>
            )}
          </div>
        </section>
        <section className="card">
          <div className="card-h">
            <h2>The replay</h2>
            <Pill tone={pill.tone} dot={pill.tone !== "plain"}>
              {pill.label}
            </Pill>
          </div>
          <div className="card-b">
            <div className="steps-done">
              {replaySteps(live).map((s) => (
                <div key={s.label} className={`sd ${s.state}`}>
                  <span className="ck">
                    <Icon name={s.state === "done" ? "check" : s.state === "missed" ? "x" : "hourglass"} />
                  </span>
                  {s.label}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {replying ? (
        <RepliesSheet
          webinarId={webinarId}
          questions={waiting}
          onClose={() => setReplying(false)}
          onSent={markAnswered}
        />
      ) : null}
      {playing !== null ? (
        <ReplaySheet
          webinarId={webinarId}
          sessionId={sessionId}
          title={title}
          startSec={playing}
          chapters={chapters}
          onClose={() => setPlaying(null)}
        />
      ) : null}
    </>
  );
}
